import React, { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../services/api'
import { toast } from 'sonner'
import {
  LinkedinLogo,
  LinkBreak,
  ArrowClockwise,
  PaperPlaneTilt,
  ChatCircle,
  ThumbsUp,
  ArrowsClockwise,
  Link,
} from '@phosphor-icons/react'
import LinkedInPublishModal from '../components/LinkedInPublishModal'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function truncate(text, max = 120) {
  if (!text) return ''
  return text.length > max ? text.slice(0, max) + '…' : text
}

export default function LinkedInPage() {
  const location = useLocation()
  const [status, setStatus] = useState(null)
  const [posts, setPosts] = useState([])
  const [projects, setProjects] = useState([])
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [connectingOAuth, setConnectingOAuth] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [linkingPostUrn, setLinkingPostUrn] = useState(null)
  const [publishModalOpen, setPublishModalOpen] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/linkedin/status')
      setStatus(data)
    } catch {
      setStatus({ connected: false })
    }
  }, [])

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true)
    try {
      const { data } = await api.get('/linkedin/posts')
      setPosts(data.posts || [])
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar posts')
    } finally {
      setLoadingPosts(false)
    }
  }, [])

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/projects')
      setProjects(data || [])
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    fetchProjects()
  }, [fetchStatus, fetchProjects])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('connected') === 'true') {
      toast.success('¡Cuenta de LinkedIn conectada!')
      window.history.replaceState({}, '', '/linkedin')
      fetchStatus()
    }
  }, [location.search, fetchStatus])

  useEffect(() => {
    if (status?.connected) fetchPosts()
  }, [status?.connected, fetchPosts])

  const handleConnect = async () => {
    setConnectingOAuth(true)
    try {
      const { data } = await api.get('/linkedin/oauth/start')
      window.location.href = data.auth_url
    } catch (err) {
      toast.error('No se pudo iniciar la conexión con LinkedIn')
      setConnectingOAuth(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de LinkedIn?')) return
    setDisconnecting(true)
    try {
      await api.delete('/linkedin/disconnect')
      setStatus({ connected: false })
      setPosts([])
      toast.success('Cuenta desconectada')
    } catch {
      toast.error('Error al desconectar')
    } finally {
      setDisconnecting(false)
    }
  }

  const handleLinkToProject = async (postUrn, projectId) => {
    if (!projectId) return
    setLinkingPostUrn(postUrn)
    try {
      await api.post(`/projects/${projectId}/linkedin-link`, { post_urn: postUrn })
      toast.success('Post vinculado al proyecto')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al vincular')
    } finally {
      setLinkingPostUrn(null)
    }
  }

  const handlePostPublished = postUrn => {
    toast.success('Post publicado en LinkedIn')
    fetchPosts()
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (status === null) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-[#0077B5] border-t-transparent animate-spin" />
      </div>
    )
  }

  // ── Not connected ─────────────────────────────────────────────────────────
  if (!status.connected) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 px-4">
        <div
          className="w-16 h-16 bg-[#0077B5]/10 flex items-center justify-center"
          style={{ borderRadius: 2 }}
        >
          <LinkedinLogo size={36} color="#0077B5" weight="fill" />
        </div>
        <div className="text-center">
          <h1 className="text-white text-xl font-semibold mb-2">Conecta tu cuenta de LinkedIn</h1>
          <p className="text-[#6E6E73] text-sm max-w-sm">
            Publica directamente desde la app y rastrea el engagement de tus posts de LinkedIn.
          </p>
        </div>
        <button
          onClick={handleConnect}
          disabled={connectingOAuth}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0077B5] hover:bg-[#005885] text-white text-sm font-medium transition-colors disabled:opacity-60"
          style={{ borderRadius: 2 }}
        >
          {connectingOAuth ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin" />
          ) : (
            <LinkedinLogo size={18} weight="fill" />
          )}
          Conectar con LinkedIn
        </button>
      </div>
    )
  }

  // ── Connected ─────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Profile card */}
      <div
        className="bg-ds-elevated border border-ds-border p-4 flex items-center gap-4"
        style={{ borderRadius: 2 }}
      >
        {status.picture ? (
          <img
            src={status.picture}
            alt={status.name}
            className="w-12 h-12 object-cover"
            style={{ borderRadius: 2 }}
          />
        ) : (
          <div
            className="w-12 h-12 bg-[#0077B5]/20 flex items-center justify-center"
            style={{ borderRadius: 2 }}
          >
            <LinkedinLogo size={24} color="#0077B5" weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{status.name || 'Mi perfil'}</p>
          <p className="text-[#6E6E73] text-xs">Cuenta conectada</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPublishModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#0077B5] border border-[#0077B5]/30 hover:bg-[rgba(0,119,181,0.1)] transition-colors"
            style={{ borderRadius: 2 }}
          >
            <PaperPlaneTilt size={14} />
            Publicar post
          </button>
          <button
            onClick={fetchPosts}
            disabled={loadingPosts}
            title="Actualizar lista"
            className="p-2 text-[#6E6E73] hover:text-white transition-colors"
          >
            <ArrowClockwise size={16} className={loadingPosts ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-ds-red border border-ds-red/30 hover:bg-[rgba(212,43,30,0.1)] transition-colors disabled:opacity-50"
            style={{ borderRadius: 2 }}
          >
            <LinkBreak size={14} />
            Desconectar
          </button>
        </div>
      </div>

      {/* Posts list */}
      <div>
        <h2 className="text-white font-medium mb-3 text-sm">
          Posts publicados{' '}
          {posts.length > 0 && <span className="text-[#6E6E73]">({posts.length})</span>}
        </h2>

        {loadingPosts ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#0077B5] border-t-transparent animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div
            className="text-center py-12 border border-dashed border-ds-border"
            style={{ borderRadius: 2 }}
          >
            <LinkedinLogo size={28} color="#6E6E73" className="mx-auto mb-2" weight="duotone" />
            <p className="text-[#6E6E73] text-sm">Aún no has publicado posts desde la app.</p>
            <button
              onClick={() => setPublishModalOpen(true)}
              className="mt-3 text-[#0077B5] text-sm hover:underline"
            >
              Publicar tu primer post
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {posts.map(post => (
              <PostRow
                key={post.post_urn}
                post={post}
                projects={projects}
                onLink={handleLinkToProject}
                linking={linkingPostUrn === post.post_urn}
              />
            ))}
          </div>
        )}

        <p className="text-[#6E6E73] text-xs mt-4">
          Las métricas de estos posts aparecerán aquí cuando LinkedIn abra el acceso a sus datos de
          rendimiento. Hasta entonces, el panel de análisis solo cubre YouTube e Instagram.
        </p>
      </div>

      {publishModalOpen && (
        <LinkedInPublishModal
          onClose={() => setPublishModalOpen(false)}
          onPublished={handlePostPublished}
        />
      )}
    </div>
  )
}

function PostRow({ post, projects, onLink, linking }) {
  const [selectedProject, setSelectedProject] = useState('')
  const mediaIcon = {
    image: '🖼️',
    document: '📄',
    article: '🔗',
    none: '',
  }

  return (
    <div
      className="bg-ds-elevated border border-ds-border p-3 flex items-start gap-3"
      style={{ borderRadius: 2 }}
    >
      {/* LinkedIn icon */}
      <div
        className="w-8 h-8 bg-[#0077B5]/10 flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ borderRadius: 2 }}
      >
        <LinkedinLogo size={16} color="#0077B5" weight="fill" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          {post.media_type && post.media_type !== 'none' && (
            <span className="text-xs">{mediaIcon[post.media_type]}</span>
          )}
          <span className="text-[#6E6E73] text-[11px]">
            {post.visibility === 'PUBLIC' ? 'Público' : 'Solo contactos'} ·{' '}
            {formatDate(post.created_at)}
          </span>
        </div>
        <p className="text-white text-sm leading-snug">{truncate(post.text)}</p>

        {/* Engagement preview */}
        {(post.likes > 0 || post.comments > 0 || post.shares > 0) && (
          <div className="flex items-center gap-3 mt-1.5">
            {post.likes > 0 && (
              <span className="flex items-center gap-1 text-[#6E6E73] text-[11px]">
                <ThumbsUp size={11} /> {post.likes}
              </span>
            )}
            {post.comments > 0 && (
              <span className="flex items-center gap-1 text-[#6E6E73] text-[11px]">
                <ChatCircle size={11} /> {post.comments}
              </span>
            )}
            {post.shares > 0 && (
              <span className="flex items-center gap-1 text-[#6E6E73] text-[11px]">
                <ArrowsClockwise size={11} /> {post.shares}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Link to project */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {post.project_id ? (
          <span className="flex items-center gap-1 text-[11px] text-[#0077B5]">
            <Link size={11} /> Vinculado
          </span>
        ) : (
          <>
            <select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              className="bg-ds-elevated border border-ds-border text-[#A1A1A6] text-xs px-2 py-1.5 focus:outline-none focus:border-[#0077B5]/50 max-w-[140px]"
              style={{ borderRadius: 2 }}
            >
              <option value="">Vincular a proyecto…</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
            <button
              onClick={() => onLink(post.post_urn, selectedProject)}
              disabled={!selectedProject || linking}
              className="px-2.5 py-1.5 bg-[rgba(0,119,181,0.1)] hover:bg-[rgba(0,119,181,0.2)] text-[#0077B5] text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderRadius: 2 }}
            >
              {linking ? '…' : 'Vincular'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
